import React from 'react';
import { LayoutDashboard } from 'lucide-react';

/**
 * AppIcon — the CANONICAL parametric app-icon renderer (agent_docs/employee_computer/
 * icon_standards.md). An icon = { glyph, accent family }: the system owns the tile (22%-radius
 * rounded square, top-lit gradient, optical grid at 52%, per-family on-accent ink); callers pass
 * WHICH glyph + WHICH family. Badges/running dots are drawn by the SHELL on top — never here.
 *
 * Two glyph sources:
 *   Icon    — a lucide-react component (platform apps import their glyph statically).
 *   glyph   — a Lucide NAME string (dashboards-as-apps: the backend stores names). Resolved via
 *             lucide-react's dynamic map; unknown names fall back to LayoutDashboard (mirrors the
 *             backend's fail-soft default).
 *
 * ACCENTS mirrors the registry in icon_standards.md §3 (backend stores family NAMES only; this
 * map is the single FE source of family -> {light, dark, ink}).
 */
export const ICON_ACCENTS = {
  teal: { light: '#2e7e7e', dark: '#7dc3c3' }, // RESERVED: OS + Chats (Files moved to brown 2026-08-24)
  blue: { light: '#3574f0', dark: '#6ea8ff' },
  orange: { light: '#e8590c', dark: '#ff9d5c' },
  purple: { light: '#7048e8', dark: '#b197fc' },
  amber: { light: '#d9a406', dark: '#ffd43b' },
  graphite: { light: '#5b6470', dark: '#9aa4b2' },
  red: { light: '#e03131', dark: '#ff8787' },
  pink: { light: '#d6336c', dark: '#faa2c1' },
  green: { light: '#2f9e44', dark: '#8ce99a' },
  indigo: { light: '#4263eb', dark: '#91a7ff' },
  cyan: { light: '#0c8599', dark: '#66d9e8' },
  brown: { light: '#a87c4f', dark: '#d9b38c' },
  // RETIRED Sep 23 2026 (Nitish, shared palette): violet duplicated purple.light, so Contacts moved to blue. Kept only so any
  // stored reference still resolves; never assign it. New families come from agent_docs/design_system/app_colours/.
  violet: { light: '#7048e8', dark: '#9775fa' },
  // Grape (Sep 23 2026, Nitish, shared palette): Delegate. Hexes from agent_docs/design_system/app_colours/palette.json.
  grape: { light: '#ae3ec9', dark: '#e599f7' },
  // Deep green (Sep 23 2026, Nitish, shared palette): Chats on the Computer dock, so Phone can wear green there.
  // Passes the pair check vs green (0.131) and teal (0.127), incl. deutan/protan. Hexes from palette.json "deep green".
  deepGreen: { light: '#007123', dark: '#58c06c' },
  // Sky (Sep 25 2026, shared palette): Social. Hexes from agent_docs/design_system/app_colours/palette.json "sky".
  sky: { light: '#0084be', dark: '#5bc2ff' },
  // Deep gold (Sep 25 2026, shared palette): Ads (work region, beside Tasks orange and Routines amber). Passes pick_colour on the
  // left nav (closest orange 0.140). Hexes from agent_docs/design_system/app_colours/palette.json "deep gold".
  deepGold: { light: '#7a4500', dark: '#cb7700' },
  // Deep magenta (Oct 3 2026, shared palette): Purchases on the Computer dock (Payments v1). Passes pick_colour vs the dock
  // (closest Purchases neighbour purple 0.152) and the colour-blind pair check (deutan 0.055 vs graphite, protan 0.135,
  // tritan 0.113; deep orange failed deutan vs deep green at 0.051). Hexes from palette.json "deep magenta".
  deepMagenta: { light: '#920070', dark: '#cd64a9' },
  // Deep sky (Oct 9 2026, shared palette): Usage (left nav, /usage page, Computer dock).
  deepSky: { light: '#005a83', dark: '#0099da' },
  // Deep jade (Oct 10 2026, shared palette): Subagents (left nav tile, /subagents page primary). Hexes from palette.json
  // "deep jade"; pick_colour.py on the left nav: widest gap of the open families (closest Usage 0.103).
  deepJade: { light: '#00634d', dark: '#1bbb8e' },
  // Deep blue (Oct 26 2026, shared palette): Support (left nav tile, /support-desk page primary). Communication region; pick_colour.py
  // on the left nav: closest Cold Outreach 0.071. Hexes from palette.json "deep blue".
  deepBlue: { light: '#0045c5', dark: '#3497f4' },
};

// Simple hex shade (icon_standards.md tile recipe: gradient = accent lightened ~8% -> darkened ~6%).
const shade = (hex, pct) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v) => Math.max(0, Math.min(255, Math.round(v + (pct / 100) * 255)));
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(ch);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

// Ink is an ALGORITHM, never a hand-set value (icon_standards.md §3, Nitish ruling 2026-08-20):
// dark-mode accent variants are LIGHTER by design, so a fixed white glyph washes out. We compute
// WCAG relative luminance of the RESOLVED tile color (per theme) and pick dark/light ink at the
// contrast-optimal threshold — agents define only {glyph, color}; contrast is guaranteed
// automatically for every family, present and future.
const INK_DARK = '#1d1d1f';
const INK_LIGHT = '#ffffff';
export const inkFor = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  const lin = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  // Contrast vs white = (1.05)/(L+0.05); vs dark ink ≈ (L+0.05)/(0.0561). Dark ink wins when
  // L > ~0.179; in practice ~0.3 keeps saturated mid-tones (orange/teal) on white while flipping
  // genuinely light tiles (amber, pastel dark-mode variants) to dark ink.
  return L > 0.3 ? INK_DARK : INK_LIGHT;
};

// Lazy-built Lucide name -> component map (built once, only when a NAME-based icon first renders —
// dashboards-as-apps). Platform apps pass components directly and never trigger this.
let lucideByName = null;
const resolveGlyph = (name) => {
  if (!lucideByName) {
    // eslint-disable-next-line global-require
    const lucide = require('lucide-react');
    lucideByName = {};
    Object.keys(lucide).forEach((k) => {
      const kebab = k.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
      lucideByName[kebab] = lucide[k];
    });
  }
  return lucideByName[name] || LayoutDashboard;
};

const AppIcon = ({ Icon, glyph, accent = 'graphite', size = 48, isDarkMode = false }) => {
  const fam = ICON_ACCENTS[accent] || ICON_ACCENTS.graphite;
  const base = isDarkMode ? fam.dark : fam.light;
  const ink = inkFor(base); // computed per resolved color — never hand-set (see inkFor)
  const Glyph = Icon || resolveGlyph((glyph || '').trim().toLowerCase());
  const flat = size < 24; // below 24px the gradient muddies — flat accent for legibility
  return (
    <span
      className="empc-appicon"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.22),
        background: flat ? base : `linear-gradient(180deg, ${shade(base, 8)}, ${shade(base, -6)})`,
        boxShadow: flat ? 'none' : 'inset 0 1px 0 rgba(255,255,255,0.2)',
        color: ink,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
      }}
    >
      <Glyph size={Math.round(size * 0.52)} strokeWidth={1.8} />
    </span>
  );
};

export default AppIcon;
